import {Location} from '@angular/common';
import {Component, ElementRef, inject, OnDestroy, OnInit, signal, ViewChild} from '@angular/core';
import {UntypedFormGroup} from '@angular/forms';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {ActivatedRoute, Router} from '@angular/router';
import {
  FormData,
  GeneratedContent,
  LabInfo,
  SkideoCreateService,
} from '@vasio-nl/skideo-create-lib';
import {Apollo, gql} from 'apollo-angular';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {ToastrService} from 'ngx-toastr';
import {
  catchError,
  combineLatest,
  delay,
  EMPTY,
  exhaustMap,
  filter,
  fromEvent,
  map,
  merge,
  shareReplay,
  startWith,
  Subject,
  Subscription,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';

import {
  TemplateSaveComponent,
  TemplateSaveContent,
} from 'src/app/shared/modals/template-save/template-save.component';
import {ErrorService} from 'src/app/shared/services/error.service';
import {TemplateService} from 'src/app/shared/services/template.service';
import {notEmpty, notNull} from 'src/app/shared/utils/utils';
import {environment} from 'src/environments/environment';

import {TemplateService_GetTemplateItem_templateItem} from '../../../shared/services/__generated__/TemplateService_GetTemplateItem';

import {BuilderForm_Get_builder} from './__generated__/BuilderForm_Get_builder';
import {BuilderForm_Get_User, BuilderForm_Get_User_me} from './__generated__/BuilderForm_Get_User';
import {BuilderForm_Jwt} from './__generated__/BuilderForm_Jwt';
import {arrowIcon, saveIcon} from './builder-form.svgs';

export enum TemplateEditMode {
  NEW = 'new',
  EDIT = 'edit',
}

type DynamicFormValues = {[key: string]: string};

const GET_BUILDER_QUERY = gql`
  query BuilderForm_Get_builder($id: ID!) {
    builderOption(id: $id) {
      id
      title
      builderConfigId
      description
      thumbnail
    }
  }`;

const GET_BUILDER_TENANT = gql`
  query BuilderForm_Get_User {
    me {
      id
      tenant {
        id
      }
    }
  }
`;

const GET_PREVIEW_JWT = gql`
  query BuilderForm_Jwt {
    previewJwt
  }
`;

@Component({
  selector: 'app-builder-form',
  templateUrl: './builder-form.component.html',
  styleUrls: ['./builder-form.component.scss'],
  standalone: false,
})
export class BuilderFormComponent implements OnInit, OnDestroy {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly apolloClient = inject(Apollo);
  private readonly createService = inject(SkideoCreateService);
  private readonly errorService = inject(ErrorService);
  private readonly locationService = inject(Location);
  private readonly modalService = inject(BsModalService);
  private readonly router = inject(Router);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly templateService = inject(TemplateService);
  private readonly toastService = inject(ToastrService);

  @ViewChild('backToTop', {static: true}) private backToTop?: ElementRef<HTMLAnchorElement>;
  @ViewChild('gallery', {static: true}) private gallery!: ElementRef<HTMLElement>;
  private readonly createFormSubject = new Subject<UntypedFormGroup>();
  private readonly generatedContentSubject = new Subject<GeneratedContent>();
  private readonly saveTemplateSubject = new Subject<void>();
  private modalReference: BsModalRef | null = null;
  private readonly subscriptions: Subscription[] = [];

  private updatedLabInfo: LabInfo | null = null;
  private templateEditMode: TemplateEditMode = TemplateEditMode.EDIT;
  protected labInfo: LabInfo | null = null;
  protected builderConfigId: string | null = null; // Needed to get the labInfo
  protected generatedContent: GeneratedContent | null = null;
  protected createForm = signal<UntypedFormGroup | null>(null);
  protected instantHost: string | null = null;
  protected me: BuilderForm_Get_User_me | null = null;
  protected parameters: {[key: string]: {value: unknown; locked: boolean}} = {};
  protected previewJwt: string | null = null;
  protected readonly arrowIcon: SafeHtml = this.sanitizer.bypassSecurityTrustHtml(arrowIcon);
  protected readonly saveIcon: SafeHtml = this.sanitizer.bypassSecurityTrustHtml(saveIcon);

  public ngOnInit(): void {
    this.instantHost = environment.instantHost;

    if (this.activatedRoute.snapshot.url[0].path === TemplateEditMode.NEW) {
      this.templateEditMode = TemplateEditMode.NEW;
    }

    // When creating a new template: idParam = builderOptionId
    // When updating existing template: idParam = templateItemId
    const idParam$ = this.activatedRoute.params.pipe(map(params => params.id as string));

    const builderOption$ = idParam$.pipe(
      filter(() => this.templateEditMode === TemplateEditMode.NEW),
      switchMap(builderOptionId =>
        this.apolloClient
          .query<BuilderForm_Get_builder>({
            query: GET_BUILDER_QUERY,
            variables: {id: builderOptionId},
          })
          .pipe(
            catchError(error => {
              this.errorService.handleDefault(error);
              return EMPTY;
            }),
            map(result => result.data?.builderOption),
            filter(notEmpty),
          ),
      ),
    );

    const templateItem$ = idParam$.pipe(
      filter(() => this.templateEditMode === TemplateEditMode.EDIT),
      switchMap(templateItemId => this.templateService.getTemplateItem$(templateItemId)),
    );

    const builderConfigId$ = merge(
      templateItem$.pipe(map(templateItem => notNull(templateItem.builderConfigId))), // Editing existing builder => builderConfigId should not be null.
      builderOption$.pipe(map(builderOption => builderOption.builderConfigId)), // Only on creating new builder
    );

    const labInfo$ = builderConfigId$.pipe(
      switchMap(builderConfigId =>
        this.createService.getLabInfo$(builderConfigId, environment.instantHost),
      ),
      shareReplay({bufferSize: 1, refCount: true}),
    );

    const previewJwt$ = this.apolloClient
      .query<BuilderForm_Jwt>({
        query: GET_PREVIEW_JWT,
      })
      .pipe(
        catchError(error => {
          this.errorService.handleDefault(error);
          return EMPTY;
        }),
        map(result => result.data?.previewJwt ?? null),
      );

    const me$ = this.apolloClient
      .watchQuery<BuilderForm_Get_User>({
        query: GET_BUILDER_TENANT,
      })
      .valueChanges.pipe(
        catchError(error => {
          this.errorService.handleDefault(error);
          return EMPTY;
        }),
        map(result => result.data?.me ?? null),
      );

    const dynamicFormChanges$ = this.createService.dynamicForm$.pipe(
      filter(notEmpty),
      switchMap((dynamicForm: UntypedFormGroup) => dynamicForm.valueChanges),
    );

    this.subscriptions.push(
      builderConfigId$.subscribe(builderConfigId => (this.builderConfigId = builderConfigId)),

      combineLatest([
        me$,
        previewJwt$,
        ...(this.templateEditMode === TemplateEditMode.EDIT ? [templateItem$] : []),
      ]).subscribe(([me, jwt, templateItem]) => {
        if (templateItem) {
          this.parameters = templateItem.parameters.edges.reduce((parameterMap, {node}) => {
            const {name, defaultValue, locked} = node;
            return {...parameterMap, [name]: {value: defaultValue, locked}};
          }, {});
        }

        this.me = me;
        this.previewJwt = jwt;
      }),

      labInfo$.subscribe(labInfo => (this.labInfo = labInfo)),

      this.createService.error$.subscribe((errorObject: {message: string}) => {
        this.toastService.error(`${errorObject.message}`);
      }),

      this.generatedContentSubject.subscribe(sourceData => {
        this.generatedContent = sourceData;
        if (this.labInfo && this.labInfo.content_type === 'video') {
          this.gallery.nativeElement.scrollIntoView({behavior: 'smooth'});
        }
      }),

      // Set initial form values based on saved data.
      this.createFormSubject
        .pipe(take(1), withLatestFrom(labInfo$, templateItem$))
        .subscribe(([form, labInfo, templateItem]) => {
          this.updateFormFromTemplateItem(form, labInfo, templateItem);
        }),

      this.createService.dynamicForm$
        .pipe(
          filter(notEmpty),
          delay(500), // Wait until all dynamic fields have been added to the form. Then send the form through once.
          take(1),
          withLatestFrom(labInfo$, templateItem$),
        )
        .subscribe(
          ([form, labInfo, templateItem]: [
            UntypedFormGroup | null,
            LabInfo,
            TemplateService_GetTemplateItem_templateItem,
          ]) => this.updateFormFromTemplateItem(notNull(form), labInfo, templateItem),
        ),

      // Editing existing template
      this.saveTemplateSubject
        .pipe(
          filter(() => this.templateEditMode === TemplateEditMode.EDIT),
          withLatestFrom(
            idParam$,
            labInfo$,
            this.createFormSubject,
            dynamicFormChanges$.pipe(startWith({})),
            templateItem$,
          ),
          filter(([, , , createForm]) => this.cropperDoneLoading(createForm)),
          filter(([, , labInfo, , dynamicFormValues]) =>
            this.hasNoReservedKeywords(labInfo, dynamicFormValues),
          ),
          exhaustMap(([, id, labInfo, createForm, dynamicFormValues, templateItem]) => {
            return this.templateService.createUpdateBuilderTemplate$(
              id,
              this.updatedLabInfo ? this.updatedLabInfo : labInfo,
              createForm,
              this.templateEditMode,
              templateItem.title,
              templateItem.createPassword,
              dynamicFormValues as DynamicFormValues,
            );
          }),
        )
        .subscribe(templateItem => {
          this.toastService.success($localize`Template edited!`);
          this.router.navigate(['templates', 'details', templateItem.id]);
        }),

      // Saving new template
      this.saveTemplateSubject
        .pipe(
          filter(() => this.templateEditMode === TemplateEditMode.NEW),
          withLatestFrom(
            idParam$,
            labInfo$,
            this.createFormSubject,
            dynamicFormChanges$.pipe(startWith({})),
          ),
          filter(([, , , createForm]) => this.cropperDoneLoading(createForm)),
          filter(([, , labInfo, , dynamicFormValues]) =>
            this.hasNoReservedKeywords(labInfo, dynamicFormValues),
          ),
        )
        .subscribe(([, id, labInfo, createForm, dynamicFormValues]) => {
          this.openTemplateSaveModal(id, labInfo, createForm, dynamicFormValues);
        }),

      fromEvent(window, 'scroll').subscribe(() => {
        if (window.scrollY > 200) {
          this.backToTop?.nativeElement.classList.add('show');
        } else {
          this.backToTop?.nativeElement.classList.remove('show');
        }
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.createService.dynamicForm$.next(null); // Makes sure the dynamic form does not persist when the builder form is closed.
  }

  protected labInfoUpdated(labInfo: LabInfo): void {
    this.updatedLabInfo = labInfo;
  }

  protected handleBackButton(): void {
    this.locationService.back();
  }

  protected handleSaveTemplateButtonClicked(): void {
    this.saveTemplateSubject.next();
  }

  protected setCreateForm(currentCreateForm: unknown): void {
    if (!this.isUntypedFormGroup(currentCreateForm)) {
      return;
    }

    this.createForm.set(currentCreateForm);
    this.createFormSubject.next(currentCreateForm);
  }

  protected handleGeneratedContentUpdated(generatedContent: unknown): void {
    if (!generatedContent || typeof generatedContent !== 'object') {
      return;
    }

    this.generatedContentSubject.next(generatedContent as GeneratedContent);
  }

  private cropperDoneLoading(createForm: UntypedFormGroup): boolean {
    const controls = createForm.controls;

    for (const key of Object.keys(controls)) {
      const control = controls[key];

      if (control.value === 'CROPPER_IS_LOADING') {
        this.toastService.error(
          $localize`Please wait a few seconds until the image has been uploaded.`,
        );
        return false;
      }
    }

    return true;
  }

  private hasNoReservedKeywords(labInfo: LabInfo, dynamicFormData: FormData): boolean {
    const inputNames = labInfo.inputs.map((input: {name: string}) => input.name);
    const dynamicFieldNames = Object.keys(dynamicFormData);
    const duplicateNames = dynamicFieldNames.filter(dynamicFieldName =>
      inputNames.includes(dynamicFieldName),
    );

    if (duplicateNames.length === 1) {
      this.toastService.error(
        $localize`The field name ${duplicateNames[0]} is a reserved keyword in this form. Please pick a different one.`,
      );
      return false;
    }

    if (duplicateNames.length > 1) {
      this.toastService.error(
        $localize`The field names ${duplicateNames
          .slice(0, -2)
          .join(', ')} and ${duplicateNames.slice(
          -1,
        )} are reserved keywords in this form. Please pick different field names.`,
      );
      return false;
    }

    return true;
  }

  private updateFormFromTemplateItem(
    form: UntypedFormGroup,
    labInfo: LabInfo,
    templateItem: TemplateService_GetTemplateItem_templateItem,
  ): void {
    const formControlNames = Object.keys(form.controls);
    const builderParameters = templateItem.parameters.edges.map(edge => edge.node);
    const imageVideoControlNames = labInfo.inputs
      .filter(
        (input: {type: string}) =>
          input.type === 'image_upload' ||
          input.type === 'video_upload' ||
          input.type === 'dynamic_reference_textarea',
      )
      .map((input: {name: string}) => input.name);

    const patchValue: FormData = {};
    builderParameters.forEach(builderParameter => {
      if (formControlNames.includes(builderParameter.name)) {
        const foundFormControl = form.controls[builderParameter.name];
        patchValue[builderParameter.name] = builderParameter.defaultValue;

        // Disable controls if they are locked, except for image/video inputs. These should always be unlocked.
        if (
          builderParameter.locked &&
          imageVideoControlNames.includes(builderParameter.name) === false
        ) {
          builderParameter.locked ? foundFormControl.disable() : foundFormControl.enable();
        }
      }
    });
    form.patchValue(patchValue);
  }

  private openTemplateSaveModal(
    id: string,
    labInfo: LabInfo,
    createForm: UntypedFormGroup,
    dynamicFormValues: FormData,
  ): void {
    this.modalReference = this.modalService.show(TemplateSaveComponent);
    const saveTemplateModalContent: TemplateSaveContent = {
      id,
      labInfo: (this.updatedLabInfo) ? this.updatedLabInfo : labInfo,
      createForm,
      dynamicFormValues,
    };
    if (this.modalReference) {
      this.modalReference.content.modalContent = saveTemplateModalContent;
    }
  }

  private isUntypedFormGroup(value: unknown): value is UntypedFormGroup {
    const formCandidate = value as {controls?: unknown} | null;
    return (
      formCandidate !== null && typeof formCandidate === 'object' && 'controls' in formCandidate
    );
  }
}
